import { Alert, AlertIcon, Divider, useToast } from "@chakra-ui/react";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { useSaveVaultProfile } from "@equidefi/portals/hooks/useVault";
import { useFormik } from "formik";
import { Heading } from "@equidefi/ui";
import React, { useEffect, useMemo, useState, useContext } from "react";
import { isValidPhoneNumber } from "react-phone-number-input";
import { useLocation, useNavigate } from "react-router";
import * as Yup from "yup";

import WorkflowProtection from "@/components/workflow/WorkflowProtection";
import { useWorkflowContext } from "@/pages/workflow/context/WorkflowContext";
import { accountTypeHelperText } from "@/pages/workflow/signers";
import { VaultContext } from "@equidefi/portals/context/VaultProvider";
import { useCurrentUser } from "@equidefi/portals/hooks/useUsers";
import { TA_ACCOUNT_TYPE_OPTIONS } from "@equidefi/shared/constants/profile";
import { Input, PhoneField, Text } from "@equidefi/ui";
import RadioGroup from "@equidefi/ui/forms/RadioGroup";
import { MESSAGE } from "../../../../../issuer/src/constants/forms";
import useWorkflowPosition from "../../../hooks/useWorkflowPosition";
import { WorkflowContainer } from "../WorkflowContainer";
import WorkflowForm from "../WorkflowForm";

const schema = Yup.object().shape({
  first_name: Yup.string()
    .trim()
    .nullable()
    .min(2, "First name must be at least 2 characters")
    .required(MESSAGE.REQUIRED("First name")),
  middle_name: Yup.string().trim().nullable(),
  last_name: Yup.string()
    .trim()
    .nullable()
    .min(2, "Last name must be at least 2 characters")
    .required(MESSAGE.REQUIRED("Last name")),
  phone_number: Yup.string()
    .nullable()
    .required(MESSAGE.REQUIRED("Mobile Phone"))
    .test({
      message: "Must be a valid phone number",
      test: (value) => (!!value ? isValidPhoneNumber(value) : false),
    }),
});

export function WorkflowProfile() {
  const { investment, offering, refetchInvestment } = useWorkflowContext();
  const { refetch: refetchVault } = useContext(VaultContext);
  const location = useLocation();
  const { data: user, refetch: refetchUser } = useCurrentUser();
  const navigate = useNavigate();
  useWorkflowPosition(1);
  useDocumentTitle(["Profile", offering?.name, investment?.issuer?.name]);

  const saveVaultProfile = useSaveVaultProfile(investment?.id);
  const toast = useToast();
  const [spinner, showSpinner] = useState(false);

  const onSubmit = async (data) => {
    let page = "subscription";

    if (location.state?.returnTo) {
      page = location.state.returnTo;
    }

    if (
      investment.vault.ta_account_type &&
      data.ta_account_type !== investment.vault.ta_account_type &&
      location.state?.returnTo
    ) {
      console.info(
        "Investor changed their account type, navigating them to signers to confirm secondaries...",
      );
      page = "signers";
    }

    showSpinner(true);

    try {
      await saveVaultProfile.mutateAsync({
        first_name: data.first_name?.trim(),
        middle_name: data.middle_name?.trim() || null,
        last_name: data.last_name?.trim(),
        phone_number: data.phone_number,
        ta_account_type: data.ta_account_type,
        ta_sub_type: data.ta_sub_type,
        entity_type: entityType,
        skip_transition: location.state?.skip_transition,
      });
      await refetchInvestment();
      await refetchVault();
      await refetchUser();
      navigate(`/offerings/${offering.slug}/${investment?.id}/${page}`);
    } catch (error) {
      console.error(error);
      toast({
        status: "error",
        description: "There was an error trying to save your profile data",
      });
      showSpinner(false);
    }
  };

  const {
    handleSubmit,
    setFieldValue,
    handleBlur,
    getFieldProps,
    errors,
    values,
    touched,
    isValid,
  } = useFormik({
    initialValues: {
      first_name: investment.vault?.first_name,
      middle_name: investment.vault?.middle_name,
      last_name: investment.vault?.last_name,
      phone_number: investment.vault?.phone_number,
      ta_account_type: investment.vault?.ta_account_type || "INDIVIDUAL",
    },
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
  });
  const entityType = useMemo(() => {
    if (
      values.ta_account_type === "INDIVIDUAL" ||
      values.ta_account_type === "JOINT"
    ) {
      return "INDIVIDUAL";
    }
    return "ENTITY";
  }, [values]);

  useEffect(() => {
    if (location.state?.refresh) {
      refetchInvestment();
    }
  }, []);

  return (
    <WorkflowContainer
      title="Investor Information"
      onContinue={handleSubmit}
      buttonProps={{
        isDisabled: !isValid,
        mt: 2,
        isLoading: spinner,
      }}>
      <WorkflowForm onSubmit={handleSubmit}>
        <WorkflowProtection vaultId={investment.vault_id} />
        <Text>How should the shares be issued?</Text>
        <RadioGroup
          fieldName="ta_account_type"
          defaultValue={values.ta_account_type}
          options={TA_ACCOUNT_TYPE_OPTIONS}
          errors={touched.ta_account_type && errors}
          setFieldValue={(field, value) => {
            setFieldValue("ta_sub_type", null);
            setFieldValue(field, value);
          }}
        />
        <Alert
          status="info"
          flexDirection="column"
          borderRadius="8"
          border="1px solid"
          borderColor="equidefi.blue"
          mt="2"
          mb="8"
          alignItems="start"
          padding="4">
          <Text m="0">
            {accountTypeHelperText[values.ta_account_type || "INDIVIDUAL"].text}
          </Text>
        </Alert>
        <Divider />
        <Heading textStyle="h1" m={0} fontWeight="700">
          Authorized Signer
        </Heading>
        <Alert
          status="info"
          borderRadius="lg"
          border="1px solid"
          borderColor="equidefi.blue"
          boxShadow="sm"
          padding="4"
          my={4}>
          <AlertIcon color="equidefi.blue" />
          <Text textStyle="body2" m={0}>
            Enter the legal name of one person only.{" "}
            {values.ta_account_type !== "INDIVIDUAL" &&
              "You will be able to add additional signers later."}
          </Text>
        </Alert>
        <Input
          label="First Name"
          placeholder="e.g. John"
          isRequired
          defaultValue={investment.vault.first_name}
          error={touched.first_name && errors.first_name}
          isInvalid={errors.first_name && touched.first_name}
          {...getFieldProps("first_name")}
        />
        <Input
          label="Middle Name"
          placeholder="e.g. David"
          defaultValue={investment.vault.middle_name}
          error={touched.middle_name && errors.middle_name}
          isInvalid={errors.middle_name && touched.middle_name}
          {...getFieldProps("middle_name")}
        />
        <Input
          label="Last Name"
          placeholder="e.g. Smith"
          isRequired
          defaultValue={investment.vault.last_name}
          error={touched.last_name && errors.last_name}
          isInvalid={errors.last_name && touched.last_name}
          {...getFieldProps("last_name")}
        />
        <PhoneField
          name="phone_number"
          error={touched?.phone_number && errors?.phone_number}
          isInvalid={errors.phone_number && touched.phone_number}
          value={values.phone_number}
          onChange={(value) => setFieldValue("phone_number", value)}
          onBlur={handleBlur}
          isRequired
          label="Mobile Phone"
        />
      </WorkflowForm>
    </WorkflowContainer>
  );
}
